import React, { useMemo, useState } from "react";
import "../../styles/FilterSidebar.css";

/* ── Icons ── */
const ChevronIcon = ({ open }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{
      transform: open ? "rotate(180deg)" : "none",
      transition: "transform 0.22s ease",
      flexShrink: 0,
    }}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const CheckIcon = () => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const ResetIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
    <path d="M3 3v5h5" />
  </svg>
);

/* ── Collapsible section ── */
const Section = ({ title, open, onToggle, children }) => (
  <div className="fs-section">
    <button className="fs-section-header" onClick={onToggle}>
      <span className="fs-section-title">{title}</span>
      <ChevronIcon open={open} />
    </button>
    {open && <div className="fs-section-body">{children}</div>}
  </div>
);

/* ── Custom checkbox row ── */
const CheckRow = ({ checked, onChange, label }) => (
  <div
    className={`fs-check-row ${checked ? "is-on" : ""}`}
    onClick={onChange}
    role="checkbox"
    aria-checked={checked}
    tabIndex={0}
    onKeyDown={(e) => (e.key === " " || e.key === "Enter") && onChange()}
  >
    <span className={`fs-checkbox ${checked ? "is-on" : ""}`}>
      {checked && <CheckIcon />}
    </span>
    <span className="fs-check-label">{label}</span>
  </div>
);

/* ════════════════════════════════════════════ */
const FilterSidebar = ({ filters, categories = [], onUpdateFilters }) => {
  /* ── Normalise categories ── */
  const normalizedCategories = useMemo(() => {
    const list = Array.isArray(categories) ? categories : [];
    return list
      .map((c) => ({
        id: Number(c?.id),
        name: c?.category_name ?? c?.name ?? "",
      }))
      .filter((c) => Number.isFinite(c.id) && c.name);
  }, [categories]);

  /* ── Safe defaults ── */
  const safe = useMemo(
    () => ({
      category: filters?.category ?? {
        all: true,
        selectedId: null,
        incomingCategory: null,
      },
      priceRange: filters?.priceRange ?? {
        min: 0,
        max: 5000,
        fromValue: 0,
        toValue: 5000,
      },
      sortBy: filters?.sortBy ?? {
        bestSellers: false,
        newItems: false,
        ourPicks: false,
      },
    }),
    [filters],
  );

  const [open, setOpen] = useState({
    categories: true,
    price: true,
    sortBy: true,
  });
  const toggle = (k) => setOpen((p) => ({ ...p, [k]: !p[k] }));

  /* ── Handlers ── */
  // ── CHANGE 1: Replace selectCategory in FilterSidebar.jsx ──

  const selectCategory = (idOrAll) => {
    if (idOrAll === "all") {
      onUpdateFilters({
        ...safe,
        category: {
          all: true,
          selectedId: null,
          selectedName: null,
          incomingCategory: null,
        },
      });
    } else {
      // Store the name alongside the id — ExploreScreen reads selectedName directly
      const cat = normalizedCategories.find((c) => c.id === Number(idOrAll));
      onUpdateFilters({
        ...safe,
        category: {
          all: false,
          selectedId: Number(idOrAll),
          selectedName: cat?.name || null, // ← this is the key fix
          incomingCategory: null,
        },
      });
    }
  };

  const handlePrice = (field, value) => {
    let from = Number(safe.priceRange.fromValue || 0);
    let to = Number(safe.priceRange.toValue || safe.priceRange.max || 5000);
    const v = Number(value);
    if (!Number.isNaN(v)) {
      if (field === "fromValue") from = v;
      else to = v;
    }
    from = Math.max(Number(safe.priceRange.min || 0), from);
    to = Math.min(Number(safe.priceRange.max || 5000), to);
    if (from > to) {
      if (field === "fromValue") to = from;
      else from = to;
    }
    onUpdateFilters({
      ...safe,
      priceRange: { ...safe.priceRange, fromValue: from, toValue: to },
    });
  };

  const setSort = (k) => {
    const already = !!safe.sortBy?.[k];
    onUpdateFilters({
      ...safe,
      sortBy: {
        bestSellers: false,
        newItems: false,
        ourPicks: false,
        ...(already ? {} : { [k]: true }),
      },
    });
  };

  const resetAll = () =>
    onUpdateFilters({
      category: { all: true, selectedId: null, incomingCategory: null },
      priceRange: { min: 0, max: 5000, fromValue: 0, toValue: 5000 },
      sortBy: { bestSellers: false, newItems: false, ourPicks: false },
    });

  /* ── Derived state ── */
  const selectedId = safe.category?.selectedId;
  const incomingLabel =
    safe.category?.incomingCategory?.category_name ??
    safe.category?.incomingCategory?.name ??
    "";
  const hasActive =
    !safe.category.all ||
    safe.priceRange.fromValue > 0 ||
    safe.priceRange.toValue < (safe.priceRange.max || 5000) ||
    safe.sortBy.bestSellers ||
    safe.sortBy.newItems ||
    safe.sortBy.ourPicks;

  const fromPct =
    ((safe.priceRange.fromValue - (safe.priceRange.min || 0)) /
      ((safe.priceRange.max || 5000) - (safe.priceRange.min || 0))) *
    100;
  const toPct =
    ((safe.priceRange.toValue - (safe.priceRange.min || 0)) /
      ((safe.priceRange.max || 5000) - (safe.priceRange.min || 0))) *
    100;

  return (
    <div className="fs-root">
      {/* ── Header ── */}
      <div className="fs-header">
        <span className="fs-header-title">Filters</span>
        {hasActive && (
          <button className="fs-reset-btn" onClick={resetAll}>
            <ResetIcon /> Reset all
          </button>
        )}
      </div>

      {/* ── Categories ── */}
      <Section
        title="Categories"
        open={open.categories}
        onToggle={() => toggle("categories")}
      >
        <div className="fs-check-list">
          <CheckRow
            checked={!!safe.category.all && !safe.category.incomingCategory}
            onChange={() => selectCategory("all")}
            label="All Products"
          />

          {safe.category.incomingCategory?.id && (
            <CheckRow
              checked={true}
              onChange={() =>
                onUpdateFilters({
                  ...safe,
                  category: {
                    all: true,
                    selectedId: null,
                    incomingCategory: null,
                  },
                })
              }
              label={incomingLabel}
            />
          )}

          {normalizedCategories.map((c) => (
            <CheckRow
              key={c.id}
              checked={
                !safe.category.all &&
                !safe.category.incomingCategory &&
                Number(selectedId) === c.id
              }
              onChange={() => selectCategory(c.id)}
              label={c.name}
            />
          ))}
        </div>
      </Section>

      {/* ── Price Range ── */}
      <Section
        title="Price Range"
        open={open.price}
        onToggle={() => toggle("price")}
      >
        <div className="fs-price-wrap">
          {/* Visual fill track */}
          <div className="fs-track">
            <div
              className="fs-track-fill"
              style={{ left: `${fromPct}%`, width: `${toPct - fromPct}%` }}
            />
          </div>

          {/* Min / Max inputs */}
          <div className="fs-price-inputs">
            <div className="fs-price-group">
              <label className="fs-price-label">Min</label>
              <div className="fs-price-field">
                <span className="fs-price-sym">$</span>
                <input
                  type="number"
                  className="fs-price-input"
                  value={safe.priceRange.fromValue}
                  min={safe.priceRange.min}
                  max={safe.priceRange.toValue}
                  onChange={(e) => handlePrice("fromValue", e.target.value)}
                />
              </div>
            </div>
            <span className="fs-price-dash">—</span>
            <div className="fs-price-group">
              <label className="fs-price-label">Max</label>
              <div className="fs-price-field">
                <span className="fs-price-sym">$</span>
                <input
                  type="number"
                  className="fs-price-input"
                  value={safe.priceRange.toValue}
                  min={safe.priceRange.fromValue}
                  max={safe.priceRange.max}
                  onChange={(e) => handlePrice("toValue", e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="fs-price-summary">
            ${safe.priceRange.fromValue.toLocaleString()} — $
            {safe.priceRange.toValue.toLocaleString()}
          </div>
        </div>
      </Section>

      {/* ── Sort By ── */}
      <Section
        title="Sort By"
        open={open.sortBy}
        onToggle={() => toggle("sortBy")}
      >
        <div className="fs-sort-list">
          {[
            { key: "bestSellers", label: "Best Sellers", icon: "🔥" },
            { key: "newItems", label: "New Arrivals", icon: "✨" },
            { key: "ourPicks", label: "Our Picks", icon: "⭐" },
          ].map(({ key, label, icon }) => (
            <button
              key={key}
              className={`fs-sort-pill ${safe.sortBy[key] ? "is-active" : ""}`}
              onClick={() => setSort(key)}
            >
              <span className="fs-pill-icon">{icon}</span>
              <span className="fs-pill-label">{label}</span>
              {safe.sortBy[key] && (
                <span className="fs-pill-check">
                  <CheckIcon />
                </span>
              )}
            </button>
          ))}
        </div>
      </Section>

      {/* ── Clear All ── */}
      <div className="fs-footer">
        <button className="fs-clear-btn" onClick={resetAll}>
          Clear All Filters
        </button>
      </div>
    </div>
  );
};

export default FilterSidebar;
